import styled from 'styled-components';

import { COLORS, PLAYERS, ANIM, SPACE } from '../../../../theme/tokens';
import { Text } from '../../../../components';

// The body content rendered inside the shared <Overlay>: identity hero, the two
// headline figure tiles and the registration meta list. The modal shell itself
// (backdrop / sheet / header / footer / scroll-lock / Escape) now belongs to
// <Overlay>; only the read-only content styling lives here.

// Vertical rhythm wrapper for the body sections (Overlay's own <Body> lays out
// its children with a smaller gap; this restores the sheet's 20px spacing).
export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// Hero: big avatar + identity.
export const Hero = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const Name = styled(Text)`
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
  color: ${PLAYERS.sheetValue};
`;

export const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const EmailText = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 500;
  color: ${PLAYERS.sheetLabel};
`;

export const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 7px;
  color: ${PLAYERS.copyGlyph};
  background: ${PLAYERS.copyBg};
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    background: ${PLAYERS.copyBgHover};
    outline: none;
  }
`;

// --- Highlighted figure tiles (Deposits / Net gaming revenue) ----------------
/** Positioned: the info panel beside the after-commission line opens under the tiles, at their full width. */
export const Tiles = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

export const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px;
  border-radius: 14px;
  background: ${PLAYERS.sheetInset};
`;

export const TileLabel = styled(Text)`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${PLAYERS.sheetLabel};
`;

export const TileValue = styled(Text)<{ $accent?: boolean }>`
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$accent ? PLAYERS.walletAccent : PLAYERS.sheetValue)};
`;

/** The tile's second line: what the brand keeps once the affiliate is paid, an estimate (muted ink, "≈"). */
export const TileNote = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${PLAYERS.meta};
  font-variant-numeric: tabular-nums;
`;

/** The info button at the end of that line. */
export const TileNoteInfo = styled.span`
  display: inline-flex;
  margin-left: ${SPACE[4]};
  vertical-align: middle;
`;

/** The amount in that line, coloured by sign: the NGR green when the brand keeps money, red when the affiliate's
 *  commission exceeds what the player brought in, the line's own ink at zero. */
export const TileNoteAmount = styled(Text)<{ $tone: 'gain' | 'loss' | 'even' }>`
  font-weight: 600;
  color: ${(p) => (p.$tone === 'gain' ? PLAYERS.walletAccent : p.$tone === 'loss' ? COLORS.danger : 'inherit')};
`;

// --- Meta list (registered, id) ---------------------------------------------
// Heading above each meta group, so the account record and the money breakdown
// read as two records rather than one long undifferentiated list.
export const GroupLabel = styled(Text)`
  display: block;
  margin-bottom: 2px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${PLAYERS.sheetLabel};
`;

/** A group heading with something beside its label (the commission group's info button). Positioned: the info
 *  panel opens under it at its full width. */
export const GroupHead = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  margin-bottom: ${SPACE[2]};
`;

export const GroupHeadLabel = styled(GroupLabel)`
  margin-bottom: 0;
`;

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const MetaList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 2px;

  &:not(:last-child) {
    border-bottom: 1px solid ${PLAYERS.sheetHairline};
  }
`;

export const MetaLabel = styled(Text)`
  font-size: 13px;
  color: ${PLAYERS.sheetLabel};
`;

export const MetaValue = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: ${PLAYERS.sheetValue};
  text-align: right;
`;

/** "N/A" / "–": the label's ink, so a row with nothing to say doesn't outshine one that has a figure. */
export const NoValue = styled(MetaValue)`
  font-weight: 500;
  color: ${PLAYERS.sheetLabel};
`;

/** An estimated figure: the muted ink and a "≈", so it never reads as a measured one. */
export const EstimateValue = styled(MetaValue)`
  color: ${PLAYERS.meta};
`;

// --- Came from ------------------------------------------------------------------
// The "Came from" value: the asset as a deep link to its composer and, under it, the landing page it went through.
export const CameValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${SPACE[2]};
  min-width: 0;
  text-align: right;
`;

export const SheetLink = styled.a`
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
  color: ${PLAYERS.sheetValue};
  text-decoration: underline;
  text-decoration-color: ${PLAYERS.sheetHairline};
  text-underline-offset: 3px;
  transition: text-decoration-color ${ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    text-decoration-color: currentColor;
    outline: none;
  }
`;

// "via <landing page>", one muted line under the asset.
export const ViaLine = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: ${SPACE[4]};
  min-width: 0;
  max-width: 100%;
  font-size: 12px;
  color: ${PLAYERS.sheetLabel};
`;

export const ViaLink = styled(SheetLink)`
  font-size: 12px;
`;
